/**
 * LoginScreen — email + password sign-in for the platform console.
 *
 * Console = separate auth realm, email/password ONLY (no OAuth). On success the
 * AuthProvider resolves the platform_users row; the route guard then drives the
 * MFA challenge. A signed-in account with no platform_users row is rejected as
 * "No autoritzat" (surfaced here from `auth.notAuthorized`).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from '@tanstack/react-router';
import { Button, Card, Field, useTheme } from '@obrava/ui';
import { CenteredCanvas, SectionTitle } from '../components/layout.js';
import { Banner } from '../components/Banner.js';
import { useAuth } from './AuthProvider.js';

export function LoginScreen(): React.ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();
  const { signIn, notAuthorized, user, platformUser } = useAuth();

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const result = await signIn(email.trim(), password);
    setSubmitting(false);
    if (!result.ok && !result.notAuthorized) {
      setError(t('platformConsole.auth:signInError'));
    }
  };

  // Already signed in as a platform user: leave /login so the AuthGate can run
  // the MFA challenge and then reveal the console. This MUST come after every
  // hook above — returning before a hook throws "rendered fewer hooks than expected".
  if (user && platformUser && !notAuthorized) {
    return <Navigate to="/tenants" replace />;
  }

  return (
    <CenteredCanvas>
      <div style={{ marginBottom: theme.spacing.space[7], textAlign: 'center' }}>
        <h1
          style={{
            margin: 0,
            fontFamily: theme.typography.family.display,
            fontSize: theme.typography.size.title1,
            fontWeight: theme.typography.weight.bold,
            letterSpacing: theme.typography.letterSpacing.tight,
            color: theme.color.textPrimary,
          }}
        >
          {t('platformConsole.auth:consoleTitle')}
        </h1>
        <p
          style={{
            margin: `${theme.spacing.space[2]}px 0 0`,
            fontFamily: theme.typography.family.sans,
            fontSize: theme.typography.size.body,
            color: theme.color.textSecondary,
          }}
        >
          {t('platformConsole.auth:consoleSubtitle')}
        </p>
      </div>

      <Card>
        <SectionTitle>{t('platformConsole.auth:signInTitle')}</SectionTitle>
        <form
          onSubmit={onSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.space[5] }}
        >
          <Field
            label={t('platformConsole.auth:email')}
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Field
            label={t('platformConsole.auth:password')}
            type="password"
            autoComplete="current-password"
            revealable
            showPasswordLabel={t('platformConsole.auth:showPassword')}
            hidePasswordLabel={t('platformConsole.auth:hidePassword')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          {notAuthorized ? (
            <Banner tone="danger">{t('platformConsole.auth:notAuthorizedBody')}</Banner>
          ) : null}
          {error ? <Banner tone="danger">{error}</Banner> : null}

          <Button type="submit" fullWidth disabled={submitting}>
            {submitting
              ? t('platformConsole.auth:signingIn')
              : t('platformConsole.auth:signInCta')}
          </Button>
        </form>
      </Card>
    </CenteredCanvas>
  );
}
